// Import-safety helpers for executable protocol blocking and inline wrapping.
// Example: `javascript:alert(1)` must be blocked before it reaches CSS output.
import { failClosedSecurityCheck } from '../../security/guard'
import { hasBlockedScheme, hasUnsafeCssPayload } from '@lentystyle/guard/shared'

// Check whether the import request uses an executable protocol.
export function isUnsafeImportRequest(request: string): boolean {
  try {
    // Return true when the request uses a blocked scheme from shared policy.
    return hasBlockedScheme(request)
  } catch (error) {
    // Return fail-closed error when import-url sanitizer cannot execute.
    return failClosedSecurityCheck('isUnsafeImportRequest', error)
  }
}

// Check whether a CSS @import statement would load an unsafe resource.
export function isUnsafeCssImportStatement(statement: string): boolean {
  try {
    // Return true when shared guard policy rejects the CSS import payload.
    return hasUnsafeCssPayload(statement)
  } catch (error) {
    // Return fail-closed error when import-statement sanitizer cannot execute.
    return failClosedSecurityCheck('isUnsafeCssImportStatement', error)
  }
}

// Build boundary-safe wrappers around inlined import source text.
export function wrapInlinedImportSource(source: string): string {
  // Check whether the inlined import source is empty.
  if (!source) {
    // Return empty content unchanged for unresolved or blank imports.
    return ''
  } else {
    // Continue boundary-safe wrapping for non-empty source.
  }

  const leading = source.startsWith('\n') ? '' : '\n'
  const trailing = source.endsWith('\n') ? '' : '\n'
  // Return source with newline boundaries around inlined content.
  return `${leading}${source}${trailing}`
}
